import { ReactNode } from 'react';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { PreApprovalStore } from '@servicetitan/financing-plans-shared';
import { isGreenSkyProvider, isSynchronyProvider } from '@servicetitan/financing-utils-shared';
import { useLoadPreApprovals } from '../../hooks/useLoadPreApprovals';

/**
 * Populates the pre-approval store for the application MFEs.
 *
 * Reads the setup data, loads the customer's latest pre-approval (skipping prequal/loan launch flows),
 * and provides it to `PreApprovalStore` so `usePreApprovalDisplay` (in plans-shared) can read from a
 * single source of truth. Other MFEs that reuse the plans components simply don't mount this loader,
 * so the store defaults to "no pre-approval" and no pre-approval components are displayed.
 */
export const PreApprovalLoader = ({ children }: { children: ReactNode }) => {
    const { setupData } = SetupDataStore.useStore();

    // Pre-approvals surface for GreenSky (a plan card via usePreApprovalDisplay) and Synchrony (a
    // Select-Plan banner via PreApprovalBanner), so gate the fetch on those two providers to
    // avoid a wasted /v1/preapprovals call on every other application launch. Reactive: switching
    // provider re-enables/disables it, and the per-customer query key is unchanged so no duplicate
    // cache entry is created. Also skip prequal/loan launch flows; those never show pre-approvals.
    const enabled =
        (isGreenSkyProvider(setupData?.provider) || isSynchronyProvider(setupData?.provider)) &&
        !setupData?.prequalificationId &&
        !setupData?.loanId;

    const { data, isLoading, isFetching } = useLoadPreApprovals(
        setupData?.tenantId ?? '',
        setupData?.customerId ?? '',
        setupData?.buId ?? '',
        { enabled },
    );

    return (
        <PreApprovalStore.Provider preApproval={data ?? null} isLoading={isLoading || isFetching}>
            {children}
        </PreApprovalStore.Provider>
    );
};
